import axios from "axios";
import { HmacSHA256 } from "crypto-js";
import { awsRegions, type RegionInfo } from "./aws-regions.js";

/**
 * Returns the key of the enum value (or null if not found).
 *
 * @param myEnum - The enum object to search.
 * @param enumValue - The enum value to find the key for.
 * @returns The key corresponding to the enum value, or null if not found.
 */
export function getEnumKeyByEnumValue(
  myEnum: Record<string, unknown>,
  enumValue: unknown
): string | null {
  const keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumValue);
  return keys.length > 0 ? keys[0] : null;
}

/**
 * Gets the region information for the given region.
 *
 * @param region - The AWS region ID to look up.
 * @returns The region information object, or undefined if not found.
 */
export function getRegionInfo(region: string): RegionInfo | undefined {
  return awsRegions.find((r) => r.id === region);
}

/**
 * Constructs a signature based on the given timestamp.
 *
 * @param timestamp - The timestamp used to construct the signature.
 * @returns The constructed SHA256 signature.
 */
export function constructSignature(timestamp: number): string {
  const precry = `${timestamp}_EC-WEB`;
  return HmacSHA256(precry, `${timestamp}`).toString();
}

/**
 * Gets the payload of a JWT token by decoding the base64-encoded middle section.
 *
 * @param token - The JWT token string.
 * @returns The decoded token payload as an object.
 */
export function getTokenPayload(token: string): Record<string, unknown> {
  const payload = token.split(".")[1];
  return JSON.parse(atob(payload)) as Record<string, unknown>;
}

/**
 * Validates the payload of a JWT token by checking for required fields.
 *
 * @param payload - The decoded JWT payload object.
 * @returns Always returns true if validation passes.
 * @throws {Error} Throws if the payload is missing or lacks the 'exp' field.
 */
export function validateTokenPayload(payload: Record<string, unknown>): true {
  if (!payload || !payload.exp) {
    throw new Error("Invalid token payload");
  }
  return true;
}

/**
 * Extracts the token from an Axios response object.
 *
 * @param response - The Axios response object.
 * @returns The token extracted from the response data.
 * @throws {Error} Throws if the response or token is missing or invalid.
 */
export function getXiTokenFromResponse(response: { data: unknown }): string {
  if (!response || typeof response.data !== "string") {
    throw new Error("Invalid token response: expected a string token in response.data");
  }
  return response.data;
}

/**
 * Requests a token from the server using HMAC-SHA256 signature authentication.
 *
 * @param tokenUrl - The URL of the token endpoint.
 * @returns A promise that resolves to the token string.
 * @throws {Error} Throws if the HTTP request fails or if token validation fails.
 */
export async function requestToken(tokenUrl: string): Promise<string> {
  const sigtime = Date.now();
  const sig = constructSignature(sigtime);
  const axconfig = {
    headers: {
      Authorization: `Bearer ${sig}`,
    },
  };
  const tokenJson = await axios.post(tokenUrl, {}, axconfig);
  const token = getXiTokenFromResponse(tokenJson);
  const payload = getTokenPayload(token);
  validateTokenPayload(payload);
  return token;
}

/**
 * Detect the browser's operating system from userAgentData if available or userAgent if not.
 *
 * @param nav - The Navigator object providing platform detection APIs.
 * @returns The operating system string, or undefined if not detected.
 */
export function detectOS(nav: Navigator): string | undefined {
  // userAgentData is the modern API (Chrome/Edge); userAgent string is the fallback.
  const uaData = (nav as Navigator & { userAgentData?: { platform: string } }).userAgentData;
  if (uaData) {
    return uaData.platform;
  }
  if (nav.userAgent.includes("Windows ")) return "Windows";
  if (nav.userAgent.includes("like Mac")) return "iOS";
  if (nav.userAgent.includes("Mac")) return "Mac OS";
  if (nav.userAgent.includes("Linux")) return "Linux";
  if (nav.userAgent.includes("Android")) return "Android";
}

/**
 * Clears the local storage so that a new token can be stored.
 *
 * @returns void
 */
export function clearLocalStorage(): void {
  localStorage.clear();
}

/**
 * Waits for a specified amount of time by wrapping setTimeout in a Promise.
 *
 * @param ms - The time to wait in milliseconds. Defaults to 1000ms.
 * @returns A promise that resolves after the specified delay.
 */
export function waitForDelay(ms = 1000): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Polls an async operation until a condition is met.
 *
 * Repeatedly calls the provided function and checks the result against a condition.
 * If the condition returns false, the polling stops and the final result is returned.
 * If the condition returns true, waits for the specified interval and polls again.
 *
 * @template T
 * @param fn - The async function to execute repeatedly.
 * @param fnCondition - Predicate that returns true to continue polling, false to stop.
 * @param ms - The time interval between polling attempts in milliseconds.
 * @param onComplete - Optional callback invoked when polling completes.
 * @returns A promise that resolves with the final result when the condition returns false.
 */
export async function pollSession<T>(
  fn: () => Promise<T>,
  fnCondition: (result: T) => boolean,
  ms: number,
  onComplete?: () => void
): Promise<T> {
  let result = await fn();
  while (fnCondition(result)) {
    await waitForDelay(ms);
    result = await fn();
  }
  if (onComplete) {
    onComplete();
  }
  return result;
}
